import React from 'react';

const DEFAULT_CASH_LIKE_SYMBOLS = ['FDRXX', 'FZFXX', 'FCASH', 'SNSXX', 'SNVXX', 'SPRXX', 'SWVXX', 'SPAXX'];

const FALLBACK_COLORS = [
    '#0d6efd', '#6610f2', '#6f42c1', '#d63384', '#dc3545',
    '#fd7e14', '#ffc107', '#198754', '#20c997', '#0dcaf0',
];

const getBarStyle = (itemName, weight, baseColor) => {
    const baseStyle = { width: `${weight}%`, transition: 'width 0.3s' };
    if (itemName === 'Excess Cash') {
        return {
            ...baseStyle,
            background: `repeating-linear-gradient(45deg, #198754, #198754 4px, #0d5c36 4px, #0d5c36 8px)`
        };
    }
    if (itemName === 'Cash Reserve') {
        return { ...baseStyle, backgroundColor: '#20c997' };
    }
    if (itemName === 'Other Holdings') {
        return {
            ...baseStyle,
            background: `repeating-linear-gradient(45deg, #dc3545, #dc3545 4px, #212529 4px, #212529 8px)`
        };
    }
    return { ...baseStyle, backgroundColor: baseColor || '#6c757d' };
};

/**
 * AccountAllocationBars
 *
 * Renders compact Before / After composition bars for a single account.
 * Percentages are computed purely from the account's holdings values (no target weighting).
 *
 * Props:
 *   holdings        - array of holding objects for this account
 *   trades          - array of trades for this account (used to compute "After")
 *   colorMap        - shared { name -> hex color } map from parent
 *   viewType        - 'sleeve' | 'security'
 *   cashLikeSymbols - array of cash-like symbol strings
 */
const AccountAllocationBars = React.memo(({
    holdings = [],
    trades = [],
    colorMap = {},
    viewType = 'sleeve',
    cashLikeSymbols,
    cashReserveMax = 0,
}) => {
    const cashSymbols = cashLikeSymbols || DEFAULT_CASH_LIKE_SYMBOLS;
    const fallbackColorsRef = React.useRef({});

    if (!holdings.length) return null;

    // ── Build per-security allocation from holdings ──────────────────────────
    const secMap = {};
    holdings.forEach((h) => {
        const rawName = h['Security Level Model Name'] || h['Symbol'] || 'Unknown';
        const symbol = h['Symbol'] || rawName;
        const isCash =
            rawName === 'UNASSIGNED CASH' ||
            rawName === 'UNASSIGNED_CASH' ||
            cashSymbols.includes(symbol);

        const name = isCash ? 'Excess Cash' : rawName;
        const sleeveName = isCash
            ? 'Excess Cash'
            : (h['Sleeve Name'] || h['Sub Model Name'] || 'Other Holdings');

        const val = h['Holdings Current Value'] || 0;

        if (!secMap[name]) {
            secMap[name] = { name, sleeveName, symbol, beforeValue: 0, afterValue: 0 };
        }
        secMap[name].beforeValue += val;
        secMap[name].afterValue += val;
    });

    // ── Apply trades to compute "after" values ───────────────────────────────
    trades.forEach((t) => {
        // Support both lowercase (SuggestedTradesTable) and PascalCase (backend) field names
        const tradeSymbol = t.symbol || t.Symbol || t.SecurityModel || t.security_model || '';
        const absVal = Math.abs(t.trade_value || t.value || t.TradeAmount || t.trade_amount || 0);
        const action = t.action || t.Action || '';
        const isBuy = action === 'BUY' || (!action && (t.quantity || 0) > 0);

        const matchKey = Object.keys(secMap).find(
            (k) => secMap[k].symbol === tradeSymbol || k === tradeSymbol
        );

        if (matchKey) {
            // Security already in holdings — update its after value
            secMap[matchKey].afterValue += isBuy ? absVal : -absVal;
        } else if (absVal > 0) {
            const modelName = t.SecurityModel || t.security_model || tradeSymbol;
            // Also try matching by model name (handles case where trade symbol differs from held symbol)
            const modelMatchKey = modelName
                ? Object.keys(secMap).find((k) => secMap[k].name === modelName || k === modelName)
                : null;
            if (modelMatchKey) {
                secMap[modelMatchKey].afterValue += isBuy ? absVal : -absVal;
            } else if (tradeSymbol && isBuy) {
                // Truly new security — create an entry so it appears in the After bar
                const sleeveName = t.SleeveName || t.sleeve_name || t.sleeveName || 'Other Holdings';
                secMap[tradeSymbol] = {
                    name: modelName || tradeSymbol,
                    sleeveName,
                    symbol: tradeSymbol,
                    beforeValue: 0,
                    afterValue: absVal,
                };
            }
        }

        // Cash always moves for any matched/unmatched trade (buys consume cash, sells produce it)
        if (absVal > 0 && secMap['Excess Cash']) {
            secMap['Excess Cash'].afterValue += isBuy ? -absVal : absVal;
        }
    });

    // ── Split cash into Cash Reserve (≤ max) and Excess Cash (> max) ────────
    if (cashReserveMax > 0 && secMap['Excess Cash']) {
        const totalCashBefore = secMap['Excess Cash'].beforeValue;
        const totalCashAfter = secMap['Excess Cash'].afterValue;

        const reserveBefore = Math.min(Math.max(totalCashBefore, 0), cashReserveMax);
        const excessBefore = Math.max(0, totalCashBefore - cashReserveMax);
        const reserveAfter = Math.min(Math.max(totalCashAfter, 0), cashReserveMax);
        const excessAfter = Math.max(0, totalCashAfter - cashReserveMax);

        secMap['Excess Cash'].beforeValue = excessBefore;
        secMap['Excess Cash'].afterValue = excessAfter;

        if (reserveBefore > 0 || reserveAfter > 0) {
            secMap['Cash Reserve'] = {
                name: 'Cash Reserve',
                sleeveName: 'Cash Reserve',
                symbol: 'CASH_RESERVE',
                beforeValue: reserveBefore,
                afterValue: reserveAfter,
            };
        }
    }

    const secItems = Object.values(secMap);
    const beforeTotal = secItems.reduce((sum, s) => sum + Math.max(s.beforeValue, 0), 0);
    const afterTotal = secItems.reduce((sum, s) => sum + Math.max(s.afterValue, 0), 0);

    if (beforeTotal <= 0) return null;

    // Compute weights
    const secAllocations = secItems.map((s) => ({
        ...s,
        beforeWeight: beforeTotal > 0 ? (Math.max(s.beforeValue, 0) / beforeTotal) * 100 : 0,
        afterWeight: afterTotal > 0 ? (Math.max(s.afterValue, 0) / afterTotal) * 100 : 0,
    }));

    // ── Aggregate to sleeves or keep per-security ────────────────────────────
    let allocations;
    if (viewType === 'sleeve') {
        const sleeveMap = {};
        secAllocations.forEach((s) => {
            const key = s.sleeveName || 'Other Holdings';
            if (!sleeveMap[key]) {
                sleeveMap[key] = { name: key, beforeWeight: 0, afterWeight: 0 };
            }
            sleeveMap[key].beforeWeight += s.beforeWeight;
            sleeveMap[key].afterWeight += s.afterWeight;
        });
        allocations = Object.values(sleeveMap).sort((a, b) => b.beforeWeight - a.beforeWeight);
    } else {
        allocations = [...secAllocations].sort((a, b) => b.beforeWeight - a.beforeWeight);
    }

    // Assign deterministic fallback colors for items not in colorMap.
    // Keep the mapping stable across renders and between Before/After bars.
    const resolveColor = (name) => {
        if (colorMap[name]) return colorMap[name];
        if (!fallbackColorsRef.current[name]) {
            const idx = Object.keys(fallbackColorsRef.current).length;
            fallbackColorsRef.current[name] = FALLBACK_COLORS[idx % FALLBACK_COLORS.length];
        }
        return fallbackColorsRef.current[name];
    };


    return (
        <div className="mt-2 pt-2 border-top" style={{ fontSize: '0.7rem' }}>
            {/* Before bar */}
            <div className="d-flex align-items-center mb-1">
                <span className="text-muted me-1" style={{ width: '10px', flexShrink: 0 }}>B</span>
                <div className="progress flex-grow-1" style={{ height: '10px' }}>
                    {allocations.map((item, idx) =>
                        item.beforeWeight > 0.1 ? (
                            <div
                                key={`b-${idx}`}
                                className="progress-bar"
                                style={getBarStyle(item.name, item.beforeWeight, resolveColor(item.name))}
                                title={`${item.name}: ${item.beforeWeight.toFixed(1)}%`}
                            />
                        ) : null
                    )}
                </div>
            </div>

            {/* After bar */}
            <div className="d-flex align-items-center">
                <span className="text-muted me-1" style={{ width: '10px', flexShrink: 0 }}>A</span>
                <div className="progress flex-grow-1" style={{ height: '10px' }}>
                    {allocations.map((item, idx) =>
                        item.afterWeight > 0.1 ? (
                            <div
                                key={`a-${idx}`}
                                className="progress-bar"
                                style={getBarStyle(item.name, item.afterWeight, resolveColor(item.name))}
                                title={`${item.name}: ${item.afterWeight.toFixed(1)}%`}
                            />
                        ) : null
                    )}
                </div>
            </div>
        </div>
    );
});

export default AccountAllocationBars;
